import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { useAppSocket, useIsSuccessful } from '@/common/hooks';
import { AppPaginationOptions, BaseQueryParams } from '@/common/interfaces';

import { DomainGatewayKeys, DomainHealthStatus, DomainsQueryKeys, useGetAllDomainsQuery, useGetByIdDomainsQuery } from '@/modules/Domains';

interface Data {
  domainId: string;
  status: DomainHealthStatus;
  checkedAt: Date | null;
  errorMessage?: string;
}

interface Args {
  domainId?: string | null;
  pageParam?: AppPaginationOptions | null;
}

export const useUpdateDomainHealthStatus = ({ domainId, pageParam }: Args) => {
  const appMessageApi = AppMessage.useControls();
  const queryClient = useQueryClient();

  const isDomainRequestSuccess = useIsSuccessful({ queryKey: [DomainsQueryKeys.GetOneById, domainId] });
  const isAllDomainsRequestSuccess = useIsSuccessful({ queryKey: [DomainsQueryKeys.GetAll, pageParam], });

  useAppSocket<Data>(DomainGatewayKeys.UpdateHealthStatus, ({ lastMessage }) => {
    const { checkedAt, domainId: id, status, errorMessage } = lastMessage;

    if (domainId) {
      if (isDomainRequestSuccess && domainId === id) {
        queryClient.setQueryData<
          BaseQueryParams<typeof useGetByIdDomainsQuery>['data']
        >([DomainsQueryKeys.GetOneById, id], (data) => {
          return {
            ...data,
            healthStatus: status,
            healthStatusCheckedAt: checkedAt,
            healthStatusErrorMessage: errorMessage
          } as BaseQueryParams<typeof useGetByIdDomainsQuery>['data'];
        });
      }

      if (status === DomainHealthStatus.NotWorking && domainId === id) {
        appMessageApi.info('Звʼязку з доменом немає, проксі-сервер буде перевірено.');
      }
    }

    if (isAllDomainsRequestSuccess) {
      queryClient.setQueryData<
        BaseQueryParams<typeof useGetAllDomainsQuery>['data']
      >([DomainsQueryKeys.GetAll, pageParam], (data) => {
        return {
          ...data,
          items: data?.items?.some(el => el.id === id)
            ? data.items.map((item) =>
              item.id === id
                ? {
                  ...item,
                  healthStatus: status,
                  healthStatusCheckedAt: checkedAt,
                  healthStatusErrorMessage: errorMessage
                }
                : item
            )
            : data?.items
        } as BaseQueryParams<typeof useGetAllDomainsQuery>['data'];
      });
    }
  });
};
